import { defineStore, skipHydrate } from 'pinia'
import flatpickr from 'flatpickr'
import { type Journey, type JourneyAndAlternative, type ArrivalDeparture } from '~/types/fptfTypes'
import { useLocalStorage } from '@vueuse/core'
import { Capacitor } from '@capacitor/core'
import dayjs from 'dayjs'
import { computed, ref } from 'vue'

export interface SearchParams {
  start: string
  destination: string
  date: string
  search_for_arrival: boolean
  only_regional: boolean
  bike: boolean
  transfer_time: number | undefined
}

export function createSearchParams(): SearchParams {
  return {
    start: '',
    destination: '',
    date: flatpickr.formatDate(new Date(), 'd.m.Y H:i'),
    search_for_arrival: false,
    only_regional: false,
    bike: false,
    transfer_time: undefined
  }
}

export interface AlphaSearchParams {
  origin: string
  destination: string
  departure: string
}

export function createAlphaSearchParams(): AlphaSearchParams {
  return {
    origin: '',
    destination: '',
    departure: new Date(new Date().setSeconds(0, 0)).toISOString() // when seconds are set, flatpickr mobile will fail on some validation
  }
}

export interface Settings {
  punctualityLimit: string
}

export function createSettings(): Settings {
  return {
    punctualityLimit: dayjs.duration(10, 'minutes').toISOString()
  }
}

async function getJourneysWeb(searchParams: SearchParams) {
  const config = useRuntimeConfig()
  return await fetch(config.public.requestBaseUrl + 'api/journeys', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(searchParams)
  })
}

async function getBoardWeb(stopName: String) {
  const config = useRuntimeConfig()
  return await fetch(config.public.requestBaseUrl + 'api/arrival-board', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ stop_name: stopName })
  })
}

async function getJourneysMobile(searchParams: SearchParams) {
  const { getRatedJourneys } = await import('~/utils/appDataGathering')

  return getRatedJourneys(searchParams)
}

async function refreshJourneysWeb(refreshToken: string) {
  const config = useRuntimeConfig()
  const response = await fetch(config.public.requestBaseUrl + 'api/refresh-journey', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ refresh_token: refreshToken })
  })

  return response
}

async function refreshJourneysMobile(journey: Journey) {
  const { refreshRatedJourney } = await import('~/utils/appDataGathering')

  return refreshRatedJourney(journey)
}

export const useMainStore = defineStore('main', () => {
  const stations = ref<string[]>([])
  const journeys = ref<Journey[]>([])
  const savedJourneys = skipHydrate(
    useLocalStorage('saved-journeys', {} as Record<string, Journey>)
  )
  const settings = skipHydrate(useLocalStorage<Settings>('settings', createSettings()))
  const progressing = ref(false)
  const disableSearch = ref(false)
  const error = ref<Error | null>(null)
  const searchParams = skipHydrate(ref(createSearchParams()))
  const alphaSearchParams = skipHydrate(ref(createAlphaSearchParams()))
  const journeysAndAlternatives = ref<JourneyAndAlternative[]>([])
  const stationBoardStation = ref('')
  const arrivals = ref<ArrivalDeparture[]>([])

  const punctualityLimitDuration = computed(() => dayjs.duration(settings.value.punctualityLimit))

  function setError(newError: Error) {
    error.value = newError
    console.log(newError)
    setTimeout(() => {
      error.value = null
    }, 15000)
  }

  function saveJourney(journey: Journey) {
    if (journey.refreshToken in savedJourneys.value) {
      delete savedJourneys.value[journey.refreshToken]
    } else {
      savedJourneys.value[journey.refreshToken] = journey
    }
  }

  async function displayFetchError(response: Response) {
    if (!response.ok) {
      let fetchError
      if (response.status === 429) {
        fetchError = Error(
          'Du hast zu viele Anfragen an unseren Server gesendet. Bitte warte ein paar Minuten und versuche es erneut. Hintergrund: https://mastodon.social/@mctoel/116301020474658146'
        )
      } else {
        try {
          fetchError = Error((await response.json()).error || response.statusText)
        } catch (e) {
          fetchError = Error(response.statusText)
        }
      }
      setError(fetchError)
      progressing.value = false
      throw fetchError
    }
    return response
  }

  async function fetchStations() {
    if (!(stations.value.length > 0)) {
      const config = useRuntimeConfig()
      let response = await fetch(config.public.requestBaseUrl + 'api/station_list.json')
      response = await displayFetchError(response)
      stations.value = (await response.json()).stations
    }
  }

  async function getJourneysAndAlternatives() {
    const config = useRuntimeConfig()
    const response = await fetch(config.public.requestBaseUrl + 'api/journeys', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(alphaSearchParams.value)
    })
    journeysAndAlternatives.value = await (await displayFetchError(response)).json()
  }

  async function refreshSavedJourney(refreshToken: string) {
    progressing.value = true
    let journey

    if (Capacitor.isNativePlatform()) {
      const response = await refreshJourneysMobile(savedJourneys.value[refreshToken])
      journey = await (await displayFetchError(response)).json()

      if (journey.length !== 1) {
        throw Error('Expected exactly one journey, got ' + journey.length)
      }
      journey = journey[0]
    } else {
      const response = await refreshJourneysWeb(refreshToken)
      journey = await (await displayFetchError(response)).json()
    }

    if (refreshToken !== journey.refreshToken) {
      savedJourneys.value[journey.refreshToken] = journey
      delete savedJourneys.value[refreshToken]
    } else {
      savedJourneys.value[refreshToken] = journey
    }

    progressing.value = false
  }

  async function getJourneys() {
    journeys.value = []
    progressing.value = true
    disableSearch.value = true

    setTimeout(() => {
      disableSearch.value = false
    }, 30000) // Disable search for 30 seconds to prevent accidental double submissions

    const response = Capacitor.isNativePlatform()
      ? await getJourneysMobile(searchParams.value)
      : await getJourneysWeb(searchParams.value)

    const fetchedJourneys = await (await displayFetchError(response)).json()
    if (fetchedJourneys) {
      journeys.value = fetchedJourneys
      navigateTo({ path: '/journey-search-result', hash: '#content' })
    }

    progressing.value = false
    disableSearch.value = false
  }

  async function getStationBoard() {
    progressing.value = true
    arrivals.value = []

    const response = await getBoardWeb(stationBoardStation.value)
    arrivals.value = await (await displayFetchError(response)).json()
    if (arrivals.value.length > 0) {
      navigateTo({ path: '/arrival-board' })
    }
    progressing.value = false
  }

  return {
    stations,
    journeys,
    savedJourneys,
    settings,
    progressing,
    disableSearch,
    error,
    searchParams,
    alphaSearchParams,
    journeysAndAlternatives,
    stationBoardStation,
    arrivals,
    punctualityLimitDuration,
    setError,
    saveJourney,
    displayFetchError,
    fetchStations,
    getJourneysAndAlternatives,
    refreshSavedJourney,
    getJourneys,
    getStationBoard
  }
})
